import { Injectable, inject } from '@angular/core';
import { AiAssistantService } from '@bizzdesign/ai-assistant/shared';
import { type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type DiagramPayload,
  type ModelerConfig,
  ModelerFormat,
  ModelerMode,
  NoViewDataError,
} from '@bizzdesign/modeler/models';
import {
  assertIsDiagramPayload,
  getEmptyDiagramPayload,
} from '@bizzdesign/modeler/utils-modeler';
import {
  type ContentForViewConfig,
  type ElementCollection,
  type ElementQuery,
  type ElementSource,
  type Population,
  TemplateType,
  type ToolInputsFactory,
} from '@bizzdesign/shared/models';
import { type Observable, catchError, filter, map, of } from 'rxjs';
import { ModelerDataService } from './modeler-data.service';

@Injectable({
  providedIn: 'root',
})
export class ModelerInputsFactoryService
  implements ToolInputsFactory<DiagramPayload>
{
  private readonly modelerDataService = inject(ModelerDataService);
  private readonly aiAssistantService = inject(AiAssistantService);

  createConfig(readonly = true): ModelerConfig {
    return readonly
      ? { mode: ModelerMode.ANALYZE, format: ModelerFormat.FREE }
      : { mode: ModelerMode.DRAW, format: ModelerFormat.FREE };
  }

  createEmptyContent(): DiagramPayload {
    return getEmptyDiagramPayload();
  }

  createContentForView(
    viewId: string,
    config?: ContentForViewConfig,
  ): Observable<{ name: string; content: DiagramPayload } | NoViewDataError> {
    const convertToFreeFormat = config?.convertToFreeFormat;
    return this.modelerDataService
      .getViewData(viewId, convertToFreeFormat)
      .pipe(
        map((result) => {
          return result instanceof NoViewDataError
            ? result
            : {
                name: result.name,
                content: {
                  ...result.payload,
                  metamodelMetadata: {
                    ...result.payload.metamodelMetadata,
                    ...(!convertToFreeFormat && {
                      targetMetamodelName:
                        result.payload.metamodelMetadata?.currentMetamodelName,
                    }),
                    ...(convertToFreeFormat && {
                      shouldTriggerAutosave: true,
                    }),
                  },
                  viewMetadata: {
                    viewId,
                    viewName: result.name,
                    isPublished: true,
                    ...(result.description === undefined
                      ? {}
                      : { description: result.description }),
                  },
                },
              };
        }),
      );
  }

  createContentForContent(content: DiagramPayload): DiagramPayload {
    return content;
  }

  createContentForElementCollection(
    collection: ElementCollection,
    templateType: TemplateType,
  ): Observable<DiagramPayload> {
    const content$ =
      templateType === TemplateType.DESIGN
        ? of(
            this.modelerDataService.getDiagramPayloadForObjects(
              collection.objects,
            ),
          )
        : this.aiAssistantService
            .sendAiRequest({
              config: { requestContent: true, requestLiveSuggestions: false },
              context: [
                { type: 'dataset', context: collection },
                {
                  type: 'selected-suggestion',
                  context: {
                    type: 'visualization',
                    id: templateType,
                    description: '',
                    previewSvg: '',
                    label: '',
                    renderer: 'modeler',
                    source: 'knowledge-base',
                  },
                },
              ],
            })
            .pipe(
              map((r) => r.content?.configuration),
              map((configuration) => {
                assertIsDiagramPayload(configuration);
                return {
                  ...configuration,
                  metamodelMetadata: {
                    ...configuration.metamodelMetadata,
                    shouldTriggerAutosave: true,
                  },
                };
              }),
              filter((c) => c != null),
              catchError((error) => {
                throw error;
              }),
            );
    return content$;
  }

  createContentForConfiguration(
    configuration: object,
    dataset: UUID[],
  ): DiagramPayload {
    assertIsDiagramPayload(configuration);
    return configuration;
  }

  createPopulatedByForPopulation(
    population: Population,
  ): Population | undefined {
    return undefined;
  }

  createSourceQueryForElementQuery(
    query: ElementQuery,
  ): ElementSource | undefined {
    return undefined;
  }
}
